import type { ApiWire } from '@/domain/shared/types';
import type { FieldPatchDto, RowCredentialDto } from '@/services/types';

import type { SecretsPatchWire } from './secretRequests';
import {
    type CredentialFlags,
    type FieldValue,
    PROVIDER_FIELD_KINDS,
    type ProviderFieldKind,
    SECRET_NAMES,
    type SecretName,
    type SecretsPatch,
} from './types';

export function isSecretName(value: string): value is SecretName {
    return SECRET_NAMES.some((name) => name === value);
}

export function isFieldKind(value: string): value is ProviderFieldKind {
    return PROVIDER_FIELD_KINDS.some((kind) => kind === value);
}

/** The DTOs mark nullable values optional, and the server sends them as null. */
export function dateOrNull(value: string | null | undefined): Date | null {
    return value ? new Date(value) : null;
}

export function fieldValueFromDto(dto: {
    text?: string | null;
    list?: string[] | null;
}): FieldValue {
    if (Array.isArray(dto.list)) return [...dto.list];
    return typeof dto.text === 'string' ? dto.text : null;
}

/** Null goes out as the key alone, which the server reads as a clear for either shape. */
export function fieldValueToDto(key: string, value: FieldValue): FieldPatchDto {
    if (value === null) return { key };
    return Array.isArray(value) ? { key, list: [...value] } : { key, text: value };
}

export function fieldValuesFromDto(
    fields: readonly { key: string; text?: string | null; list?: string[] | null }[]
): Record<string, FieldValue> {
    return Object.fromEntries(fields.map((field) => [field.key, fieldValueFromDto(field)]));
}

export function fieldPatches(values: Record<string, FieldValue>): FieldPatchDto[] {
    return Object.entries(values).map(([key, value]) => fieldValueToDto(key, value));
}

export function credentialFlags(
    credentials: readonly ApiWire<RowCredentialDto>[]
): CredentialFlags {
    const flags: CredentialFlags = {};
    for (const credential of credentials)
        if (isSecretName(credential.name)) flags[credential.name] = credential.set;
    return flags;
}

/** Undefined when nothing was typed or cleared, so the request leaves `secrets` out. */
export function secretsPatchToDto(patch: SecretsPatch): SecretsPatchWire | undefined {
    const dto: SecretsPatchWire = {};
    let present = false;
    for (const name of SECRET_NAMES) {
        const value = patch[name];
        if (value === undefined) continue;
        dto[name] = value;
        present = true;
    }
    return present ? dto : undefined;
}

/**
 * A value as the server stores it: trimmed, blank list entries dropped, an empty value null.
 * The save compares values this way, so a stray space is not an edit.
 */
export function storedValue(value: FieldValue | undefined): FieldValue {
    if (value === undefined || value === null) return null;
    if (typeof value === 'string') {
        const text = value.trim();
        return text === '' ? null : text;
    }
    const items = value.map((entry) => entry.trim()).filter((entry) => entry !== '');
    return items.length > 0 ? items : null;
}

export function sameStoredValue(a: FieldValue | undefined, b: FieldValue | undefined): boolean {
    const left = storedValue(a);
    const right = storedValue(b);
    if (left === null || right === null) return left === right;
    if (typeof left === 'string' || typeof right === 'string') return left === right;
    return left.length === right.length && left.every((entry, index) => entry === right[index]);
}
